import { useMemo } from 'react'
import { Col, Button, Checkbox } from 'antd'

import DiscountInput from './DiscountInput'
import { TranslationKeys } from '../../Constants/TranslationKeys'
import {
  retrieveChannelMatchWithPricelist,
  retrieveSiteMatchWithPriceList,
  retrieveStoresMatchWithPricelist,
} from '../../Helpers/DiscountHelper'
import { renderTranslation } from '../../Helpers/GenericHelpers'
import {
  TypeOfChannel,
  resetPriceListsToCopyTo,
  setActivePriceListToBeCopy,
  setListingCopyPopover,
} from '../../Slices/Omnichannel/DiscountSlice'
import { useAppDispatch, useAppSelector } from '../../hooks'
import ActiveListingPopover from 'Components/Table/TableComponents/ActiveListingPopover'
import { getPricelistName } from 'Helpers/PricesHelper'

type Props = {
  sectorIndex: number
  sectorArray: any
  channelIndex: number
  channelArray: any
  channel: any
  superGroup: string
  sector: any
  priceListsToCopyTo: any
  superGroupId: number
  editDisabled: boolean
  sectorDiscounts: Map<string, any>
  priceListDiscounts: Map<string, any>
  brandDiscounts: Map<string, any>
  width: number
  showSettingsView: () => unknown
  sendPriceListDiscountsCopyByChannel: () => unknown
  closeCopyPopover: () => unknown
  checkPriceListToCopyTo: (superGroupId: number, sectorId: number, channel: string) => unknown
}

export default function DiscountSuperGroupTableHeader(props: Props) {
  const dispatch = useAppDispatch()
  const priceListToCopyFrom = useAppSelector(state => state.present.discount.priceListToCopyFrom)
  const channelList = useAppSelector(state => state.present.channel.channelList)
  const listings = useAppSelector(state => state.present.configuration.allPricelists)

  const listingCopyPopoverSelected = useAppSelector(state => state.present.discount.listingCopyPopoverSelected)
  const language = useAppSelector(state => state.present.language.value)

  const channelType = useAppSelector(state => state.present.discount.typeOfChannel)
  const pricelists = useAppSelector(state => state.present.site.pricelists)
  const stores = useAppSelector(state => state.present.store.stores)

  const listingInformation = listings.find(x => x.name === props.channel.channel)
  const listingInformationFrom = listings.find(x => x.name === priceListToCopyFrom.channel)

  const isListingTypeSell = (listingInformation && listingInformation.isSell) || !listingInformation
  const isListingTypeSellFrom = (listingInformationFrom && listingInformationFrom.isSell) || !listingInformationFrom

  const priceListCopyActive = Object.keys(priceListToCopyFrom).length > 0
  const priceListCopyMode =
    priceListCopyActive &&
    priceListToCopyFrom.superGroup === props.superGroupId &&
    priceListToCopyFrom.sector === props.sector.id &&
    priceListToCopyFrom.channel === props.channel.channel

  const listingCopyPopoverOpenCheck =
    listingCopyPopoverSelected.superGroup === props.superGroupId &&
    listingCopyPopoverSelected.sector === props.sector.id &&
    listingCopyPopoverSelected.channel === props.channel.channel

  const listToShowByType = useMemo(() => {
    switch (channelType) {
      case TypeOfChannel.Marketplace:
        const channelBaseOnPricelist = retrieveChannelMatchWithPricelist(props.channel.channel, channelList)
        return (
          <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
            {channelBaseOnPricelist.map(x => {
              return (
                <div
                  key={props.superGroup + '-' + props.sector.name + '-' + props.channel.channel + '-' + x.name}
                  className='pop-list exclude-modal-trigger'
                >
                  {x.name}
                </div>
              )
            })}
          </div>
        )
      case TypeOfChannel.Site:
        const siteBasedOnPricelist = retrieveSiteMatchWithPriceList(props.channel.channel, pricelists)
        return (
          <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
            {siteBasedOnPricelist.map(x => {
              return (
                <div
                  key={props.superGroup + '-' + props.sector.name + '-' + props.channel.channel + '-' + x}
                  className='pop-list exclude-modal-trigger'
                >
                  {x}
                </div>
              )
            })}
          </div>
        )
      case TypeOfChannel.Store:
        const storesBasedOnPricelist = retrieveStoresMatchWithPricelist(props.channel.channel, stores)
        return (
          <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
            {storesBasedOnPricelist.map(x => {
              return (
                <div
                  key={props.superGroup + '-' + props.sector.name + '-' + props.channel.channel + '-' + x}
                  className='pop-list exclude-modal-trigger'
                >
                  {x}
                </div>
              )
            })}
          </div>
        )
      default:
        return []
    }
  }, [channelList, pricelists, props.channel.channel, props.sector.name, props.superGroup, stores, channelType])

  const copyMode = useMemo(
    () => (
      <Col span={24} className='exclude-modal-trigger'>
        {priceListCopyMode ? (
          <div className='exclude-modal-trigger'>
            <Button
              className='btn standard-btn exclude-modal-trigger'
              onClick={props.sendPriceListDiscountsCopyByChannel}
              disabled={props.priceListsToCopyTo?.length === 0}
            >
              {renderTranslation(language, TranslationKeys.listingPopApply)}
            </Button>
            <Button className='btn alt-btn exclude-modal-trigger ml-2' onClick={props.closeCopyPopover}>
              {renderTranslation(language, TranslationKeys.listingPopCancel)}
            </Button>
          </div>
        ) : props.editDisabled ? null : (
          <div
            className='cursor-pointer exclude-modal-trigger  standard-btn popover-hover'
            onClick={() => {
              if (priceListCopyActive || props.editDisabled) {
                //props?
                return
              }

              dispatch(
                setActivePriceListToBeCopy({
                  superGroup: props.superGroupId,
                  sector: props.sector.id,
                  channel: props.channel.channel,
                }),
              )
              dispatch(resetPriceListsToCopyTo())
            }}
          >
            {renderTranslation(language, TranslationKeys.listingPopCopyDiscountValue) +
              ' ' +
              getPricelistName(props.channel.channel, language)}
          </div>
        )}
      </Col>
    ),
    [
      priceListCopyMode,
      language,
      dispatch,
      priceListCopyActive,
      props.channel.channel,
      props.closeCopyPopover,
      props.editDisabled,
      props.priceListsToCopyTo?.length,
      props.sector.id,
      props.sendPriceListDiscountsCopyByChannel,
      props.superGroupId,
    ],
  )

  const getPopupContainer = (triggerNode: HTMLElement) => {
    const elementToAttach = document.getElementById('discount-tables-container')
    return elementToAttach
  }

  const openAction = open => {
    if (priceListCopyMode) {
      return
    }
    if (!open) {
      dispatch(setListingCopyPopover({ superGroup: null, sector: null, channel: null }))
    }
  }

  const popoverTriggerContent = useMemo(
    () => (
      <div
        className='pb-1 text-underline cursor-pointer exclude-modal-trigger'
        onClick={() => {
          dispatch(
            setListingCopyPopover({
              superGroup: props.superGroupId,
              sector: props.sector.id,
              channel: props.channel.channel,
            }),
          )
        }}
      >
        {getPricelistName(props.channel.channel, language)}
      </div>
    ),
    [dispatch, props.channel.channel, props.sector.id, props.superGroupId],
  )

  return (
    <div className={'super-group-channel-head-box'} style={{ width: props.width - 5 }}>
      <ActiveListingPopover
        channelType={channelType}
        customListToShowByType={listToShowByType}
        activeListing={props.channel.channel}
        showPopoverContent={!priceListCopyMode}
        showSettingView={props.showSettingsView}
        id={'popover-channel'}
        additionalContent={copyMode}
        openAction={listingCopyPopoverOpenCheck}
        onOpenChangeAction={openAction}
        getPopupContainer={getPopupContainer}
        popoverTriggerContent={popoverTriggerContent}
      />

      <div className='channel-input-box exclude-modal-trigger'>
        {!priceListCopyActive || priceListCopyMode || isListingTypeSell !== isListingTypeSellFrom ? (
          <DiscountInput
            superGroup={props.superGroupId}
            sector={props.sector.id}
            channel={props.channel.channel}
            brand={null}
            sectorDiscounts={props.sectorDiscounts}
            priceListDiscounts={props.priceListDiscounts}
            brandDiscounts={props.brandDiscounts}
            editDisabled={props.editDisabled}
          />
        ) : (
          <Checkbox
            className='season-listing-checkbox general-checkbox'
            onChange={e => {
              props.checkPriceListToCopyTo(props.superGroupId, props.sector.id, props.channel.channel)
            }}
            checked={props.priceListsToCopyTo?.find(
              x =>
                x.superGroup === props.superGroupId &&
                x.sector === props.sector.id &&
                x.channel === props.channel.channel,
            )}
          ></Checkbox>
        )}
      </div>
    </div>
  )
}
